/**
 * Global DOM patch to prevent browser extensions (such as Google Translate,
 * Grammarly, LanguageTool, password managers, etc.) from crashing React.
 *
 * When an extension mutates the DOM (e.g. Google Translate wrapping TextNodes in <font>
 * or Grammarly injecting helper spans), React's Virtual DOM reconciliation can lose track
 * of actual node parents. When React attempts to call parent.removeChild(node) or
 * parent.insertBefore(node, ref), the browser natively throws a DOMException:
 *   "NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node."
 *
 * By intercepting removeChild, insertBefore, and replaceChild, we safely handle these
 * foreign mutations without throwing an uncaught exception, preventing the entire
 * application from crashing.
 */

if (typeof window !== "undefined" && typeof Node === "function" && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      if (console) {
        console.warn(
          "Node.prototype.removeChild: child is not a direct child of parent. Ignoring to prevent extension crash.",
          child,
          this
        );
      }
      if (child.parentNode) {
        try {
          return child.parentNode.removeChild(child) as T;
        } catch {
          // Already removed or detached by extension
        }
      }
      return child;
    }
    try {
      return originalRemoveChild.call(this, child) as T;
    } catch (error) {
      if (console) {
        console.warn(
          "Node.prototype.removeChild: caught native error, ignoring to prevent extension crash.",
          error
        );
      }
      return child;
    }
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (console) {
        console.warn(
          "Node.prototype.insertBefore: referenceNode is not a child of parent. Appending instead to prevent extension crash.",
          referenceNode,
          this
        );
      }
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    try {
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    } catch (error) {
      if (console) {
        console.warn(
          "Node.prototype.insertBefore: caught native error, appending instead to prevent extension crash.",
          error
        );
      }
      return this.appendChild(newNode) as unknown as T;
    }
  };

  const originalReplaceChild = Node.prototype.replaceChild;
  Node.prototype.replaceChild = function <T extends Node>(
    newChild: Node,
    oldChild: T
  ): T {
    if (oldChild.parentNode !== this) {
      if (console) {
        console.warn(
          "Node.prototype.replaceChild: oldChild is not a child of parent. Appending newChild to prevent extension crash.",
          oldChild,
          this
        );
      }
      return this.appendChild(newChild) as unknown as T;
    }
    try {
      return originalReplaceChild.call(this, newChild, oldChild) as T;
    } catch (error) {
      if (console) {
        console.warn(
          "Node.prototype.replaceChild: caught native error, appending instead to prevent extension crash.",
          error
        );
      }
      return this.appendChild(newChild) as unknown as T;
    }
  };
}

export {};
